// Sperre, solange die E-Mail-Adresse nicht bestätigt ist.
//
// Ohne bestätigte Adresse erreichen den Betrieb weder Anmeldecodes noch
// Rechnungen noch die Ankündigung einer Lastschrift — und die ist vor jedem
// Einzug Pflicht. Deshalb steht die Bestätigung vor der Anwendung und nicht
// als Hinweis daneben, den man wegklickt.
//
// Keine Navigation, kein Abbrechen: es gibt genau zwei Wege weiter — den Link
// in der Mail oder eine neue Mail anfordern.
import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Hinweis, Karte, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { LogoKachel } from '../../firma/komponenten/Logo';

export default function MailBestaetigen() {
  const { t } = useSprache();
  const { benutzer, mailBestaetigung, abmelden, aktualisieren } = useAuth();
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function nochmal() {
    setSendet(true);
    setFehler(null);
    setMeldung(null);
    try {
      const r = await api<{ geschickt: boolean; an: string }>('/auth/bestaetigung/erneut', { methode: 'POST' });
      setMeldung(r.geschickt ? t('bestaetigung.erneutGesendet') : t('bestaetigung.versandFehlt'));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('bestaetigung.fehlgeschlagen'));
    } finally { setSendet(false); }
  }

  const adresse = mailBestaetigung?.an || benutzer?.email || '';

  return (
    <div
      style={{
        // Oben die Statusleiste, unten die Home-Leiste freihalten; im Browser
        // sind beide Werte 0 und es bleibt bei den 3rem von vorher.
        paddingTop: 'calc(3rem + var(--sicher-oben))',
        paddingBottom: 'calc(3rem + var(--sicher-unten))',
      }}
      className="flex min-h-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <LogoKachel groesse="gross" klasse="mx-auto mb-4 shadow-lg shadow-marke-600/25" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('bestaetigung.titel')}</h1>
        </div>

        <Karte>
          <div className="space-y-4 p-6">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('bestaetigung.text')}
            </p>
            {adresse && (
              <p className="rounded-lg bg-slate-100 px-3 py-2 text-center font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100">
                {adresse}
              </p>
            )}
            <Hinweis art="info">{t('bestaetigung.warum')}</Hinweis>

            <Erfolgsmeldung text={meldung} />
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

            <div className="flex flex-col gap-2">
              <Knopf variante="primaer" onClick={() => aktualisieren()}>
                {t('bestaetigung.schonBestaetigt')}
              </Knopf>
              <Knopf onClick={nochmal} laedt={sendet}>{t('bestaetigung.erneut')}</Knopf>
            </div>

            <p className="text-center text-xs text-slate-500 dark:text-slate-400">
              {t('bestaetigung.spam')}
            </p>

            <button type="button" onClick={abmelden}
                    className="w-full text-center text-xs text-slate-500 hover:underline dark:text-slate-400">
              {t('aktion.abmelden')}
            </button>
          </div>
        </Karte>
      </div>
    </div>
  );
}
